/**
 * Per-attachment state machine + key vault for view-once media.
 *
 * Mirrors iOS `ViewOnceStore.swift` with IndexedDB replacing the JSON-file
 * per-mediaId persistence strategy.
 *
 * # Security model
 * The state machine is the security boundary.  The key bytes live inside the
 * manifest field of ViewOnceRecord, which is dropped (set to null) the moment
 * a record transitions out of `unviewed`/`viewing`.  Once the IndexedDB entry
 * is overwritten without the manifest field, the key is no longer recoverable
 * from storage by any caller — we never fan it out into a separate key vault
 * that would have to be wiped twice.
 *
 * # Key wipe order
 * 1. Build the new record with `manifest: null` in memory.
 * 2. Open an IndexedDB `readwrite` transaction.
 * 3. Overwrite the existing record (put).
 * 4. Close the transaction (implicit commit) — disk is updated before the
 *    promise resolves.
 * The old key bytes are now unreachable from any persistent storage; the
 * in-memory `CryptoKey` objects (if any) are not held by the store layer.
 *
 * # Crash recovery
 * `recoverInterruptedSessions()` is called at app-launch (before any chat view
 * renders). Any record found in `viewing` state is force-advanced to `viewed`
 * with the manifest dropped.  The "shipped a key, then forgot we did" failure
 * mode is impossible: even if the process dies between beginConsume() and
 * finishConsume(), the next launch will wipe the key.
 *
 * # IndexedDB schema
 * Database name: `kaether_view_once` (override via constructor for tests).
 * Object store: `records` — keyed by `mediaId` (string).
 * Record shape: `ViewOnceRecordJSON` (manifest.key base64-encoded so it
 * serialises cleanly).
 */

import {
  type ViewOnceRecord,
  type ViewOnceManifest,
  type ViewOnceState,
  type ViewOnceDirection,
  type ViewOnceRecordJSON,
  recordToJSON,
  recordFromJSON,
  transitioning,
  ViewOnceNotFoundError,
  ViewOnceWrongStateError,
  ViewOnceStoreError,
  VIEW_ONCE_MANIFEST_VERSION,
} from "./ViewOnceMedia";

export const VIEW_ONCE_DB_NAME = "kaether_view_once" as const;
export const VIEW_ONCE_STORE_NAME = "records" as const;
const DB_VERSION = 1;

// ── Low-level IndexedDB helpers ───────────────────────────────────────────────

function openDB(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = (ev) => {
      const db = (ev.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(VIEW_ONCE_STORE_NAME)) {
        db.createObjectStore(VIEW_ONCE_STORE_NAME, { keyPath: "mediaId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () =>
      reject(new ViewOnceStoreError(`IndexedDB open failed: ${req.error?.message}`));
  });
}

function idbGet(
  db: IDBDatabase,
  mediaId: string,
): Promise<ViewOnceRecordJSON | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(VIEW_ONCE_STORE_NAME, "readonly");
    const req = tx.objectStore(VIEW_ONCE_STORE_NAME).get(mediaId);
    req.onsuccess = () => resolve(req.result as ViewOnceRecordJSON | undefined);
    req.onerror = () =>
      reject(new ViewOnceStoreError(`IndexedDB get failed: ${req.error?.message}`));
  });
}

function idbPut(db: IDBDatabase, record: ViewOnceRecordJSON): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(VIEW_ONCE_STORE_NAME, "readwrite");
    const req = tx.objectStore(VIEW_ONCE_STORE_NAME).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () =>
      reject(new ViewOnceStoreError(`IndexedDB put failed: ${req.error?.message}`));
  });
}

function idbGetAll(db: IDBDatabase): Promise<ViewOnceRecordJSON[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(VIEW_ONCE_STORE_NAME, "readonly");
    const req = tx.objectStore(VIEW_ONCE_STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result as ViewOnceRecordJSON[]);
    req.onerror = () =>
      reject(new ViewOnceStoreError(`IndexedDB getAll failed: ${req.error?.message}`));
  });
}

function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(VIEW_ONCE_STORE_NAME, "readwrite");
    const req = tx.objectStore(VIEW_ONCE_STORE_NAME).clear();
    req.onsuccess = () => resolve();
    req.onerror = () =>
      reject(new ViewOnceStoreError(`IndexedDB clear failed: ${req.error?.message}`));
  });
}

// ── ViewOnceStore ─────────────────────────────────────────────────────────────

/**
 * IndexedDB-backed state machine for view-once attachments.
 *
 * All mutating methods persist BEFORE returning. A caller that crashes between
 * `beginConsume` returning and `finishConsume` being called will leave the
 * record in `viewing` state on disk; `recoverInterruptedSessions()` at next
 * launch force-advances it to `viewed` with the manifest dropped.
 */
export class ViewOnceStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName = VIEW_ONCE_DB_NAME) {
    this.dbName = dbName;
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDB(this.dbName);
    }
    return this.dbPromise;
  }

  private async readRecord(mediaId: string): Promise<ViewOnceRecord | null> {
    const db = await this.db();
    const json = await idbGet(db, mediaId);
    return json != null ? recordFromJSON(json) : null;
  }

  private async writeRecord(record: ViewOnceRecord): Promise<void> {
    const db = await this.db();
    await idbPut(db, recordToJSON(record));
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  /**
   * Register a freshly-received or freshly-sent view-once attachment.
   * Idempotent on `mediaId`: a second call returns the existing record rather
   * than overwriting it (which would silently re-arm a consumed message).
   */
  async register(
    manifest: ViewOnceManifest,
    direction: ViewOnceDirection,
    now = new Date().toISOString(),
  ): Promise<ViewOnceRecord> {
    const existing = await this.readRecord(manifest.mediaId);
    if (existing != null) return existing;

    const record: ViewOnceRecord = {
      mediaId: manifest.mediaId,
      direction,
      state: "unviewed",
      manifest,
      createdAt: now,
      lastUpdated: now,
    };
    await this.writeRecord(record);
    return record;
  }

  // ── Consume (security-critical path) ─────────────────────────────────────────

  /**
   * Atomically transition `unviewed → viewing` and return the manifest.
   * Any state other than `unviewed` throws — this is the single source of
   * "one-shot" enforcement.
   *
   * Persistence happens BEFORE return so a crash after this call cannot
   * rewind the state back to `unviewed`.
   */
  async beginConsume(
    mediaId: string,
    now = new Date().toISOString(),
  ): Promise<ViewOnceManifest> {
    const record = await this.readRecord(mediaId);
    if (record == null) throw new ViewOnceNotFoundError(mediaId);
    if (record.state !== "unviewed") {
      throw new ViewOnceWrongStateError(["unviewed"], record.state);
    }
    if (record.manifest == null) {
      // `.unviewed` with a null manifest is a corrupted record — fail closed.
      throw new ViewOnceStoreError("unviewed record missing manifest");
    }

    // Persist the `viewing` state BEFORE returning the key.
    const viewing = transitioning(record, "viewing", false, now);
    await this.writeRecord(viewing);
    return record.manifest;
  }

  /**
   * Transition `viewing → viewed`, dropping the manifest (and key) in the
   * same write. After this call, the key is unrecoverable from IndexedDB.
   *
   * Wipe order: (1) build record with `manifest: null`, (2) put to IndexedDB,
   * (3) transaction commits. The old key bytes on disk are overwritten before
   * this promise resolves.
   */
  async finishConsume(
    mediaId: string,
    now = new Date().toISOString(),
  ): Promise<void> {
    const record = await this.readRecord(mediaId);
    if (record == null) throw new ViewOnceNotFoundError(mediaId);
    if (record.state !== "viewing") {
      throw new ViewOnceWrongStateError(["viewing"], record.state);
    }
    const viewed = transitioning(record, "viewed", true /* dropManifest */, now);
    await this.writeRecord(viewed);
  }

  /**
   * Force-wipe a record from any non-terminal state. Used by outgoing-side
   * cancellation, expiry, and "user tapped delete." Always drops the manifest.
   * Idempotent on a record already in `wiped`.
   */
  async wipe(mediaId: string, now = new Date().toISOString()): Promise<void> {
    const record = await this.readRecord(mediaId);
    if (record == null) return;
    if (record.state === "wiped") return;
    const wiped = transitioning(record, "wiped", true /* dropManifest */, now);
    await this.writeRecord(wiped);
  }

  /**
   * Mark a record `expired` (server reported the ciphertext is gone).
   * Drops the manifest because there is nothing left to decrypt.
   */
  async markExpired(
    mediaId: string,
    now = new Date().toISOString(),
  ): Promise<void> {
    const record = await this.readRecord(mediaId);
    if (record == null) return;
    if (record.state === "expired" || record.state === "wiped") return;
    const expired = transitioning(record, "expired", true /* dropManifest */, now);
    await this.writeRecord(expired);
  }

  /**
   * Crash-during-reveal recovery. Any record stuck in `viewing` on startup is
   * force-advanced to `viewed` with the manifest dropped. From the user's point
   * of view: the reveal happened (the key was returned at least once); we will
   * never reveal it a second time.
   *
   * Call this at app-launch before the chat view renders.
   */
  async recoverInterruptedSessions(
    now = new Date().toISOString(),
  ): Promise<string[]> {
    const db = await this.db();
    const all = await idbGetAll(db);
    const recovered: string[] = [];
    for (const json of all) {
      if (json.state === "viewing") {
        const record = recordFromJSON(json);
        const viewed = transitioning(record, "viewed", true /* dropManifest */, now);
        await this.writeRecord(viewed);
        recovered.push(record.mediaId);
      }
    }
    return recovered;
  }

  // ── Queries ───────────────────────────────────────────────────────────────────

  async record(mediaId: string): Promise<ViewOnceRecord | null> {
    return this.readRecord(mediaId);
  }

  async state(mediaId: string): Promise<ViewOnceState | null> {
    const r = await this.readRecord(mediaId);
    return r?.state ?? null;
  }

  async manifest(mediaId: string): Promise<ViewOnceManifest | null> {
    const r = await this.readRecord(mediaId);
    return r?.manifest ?? null;
  }

  async allRecords(): Promise<ViewOnceRecord[]> {
    const db = await this.db();
    const all = await idbGetAll(db);
    return all.map(recordFromJSON);
  }

  /** Clear all records. For tests only. */
  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClear(db);
  }

  /**
   * Returns the raw JSON string stored in IndexedDB for the given mediaId.
   * Used by the self-test to assert that wiped records contain zero key bytes.
   */
  async rawDiskJSON(mediaId: string): Promise<string | null> {
    const db = await this.db();
    const json = await idbGet(db, mediaId);
    return json != null ? JSON.stringify(json) : null;
  }
}

// Re-export types needed by consumers of this module.
export type {
  ViewOnceRecord,
  ViewOnceManifest,
  ViewOnceState,
  ViewOnceDirection,
  ViewOnceRecordJSON,
  ViewOnceManifestJSON,
} from "./ViewOnceMedia";
export {
  VIEW_ONCE_MANIFEST_VERSION,
  ViewOnceNotFoundError,
  ViewOnceWrongStateError,
  ViewOnceStoreError,
} from "./ViewOnceMedia";
